import { BoxV2 as Box, Button, Flex } from 'portal-commons';
import { FunctionComponent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';

import CarrierStatus from './CarrierStatus';
import { useControllers } from './hooks';
import { CarrierTermsFormValues } from './types';
import { FormActionLayout, FormContainerLayout } from '../FormLayout';
import FormSection from '../FormSection';
import { MnoMetadata } from '@/apis/core';
import { DRAFT_RCS_CAMPAIGN_ENABLED } from '@/constants';

interface CarrierTermsFormProps {
  carriers?: MnoMetadata[];
  defaultValues?: CarrierTermsFormValues;
  onChange?: (values: CarrierTermsFormValues) => void;
  onPrevious?: () => void;
  onSave?: () => void;
  onNext?: () => void;
}

const CarrierTermsForm: FunctionComponent<CarrierTermsFormProps> = ({
  carriers = [],
  defaultValues,
  onChange,
  onPrevious,
  onSave,
  onNext,
}) => {
  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
  } = useForm<CarrierTermsFormValues>({
    defaultValues: {
      mnoIds: [],
      ...defaultValues,
    },
  });
  const { mnoIds, setMnoIds, mnoIdsError } = useControllers(control);

  const handleValid = () => {
    onNext?.();
  };

  const handleCarrierToggle = (carrierId: string) => {
    const index = mnoIds.indexOf(carrierId);
    const newMnoIds = [...mnoIds];
    if (index === -1) {
      newMnoIds.push(carrierId);
    } else {
      newMnoIds.splice(index, 1);
    }
    setMnoIds(newMnoIds);
  };

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      onChange?.(values as CarrierTermsFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  useEffect(() => {
    const newMnoIds = carriers
      .filter((carrier) => carrier.qualify)
      .map((carrier) => carrier.id);
    setMnoIds(newMnoIds);
  }, [carriers]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <FormContainerLayout>
        <FormSection
          icon={faShareNodes}
          title="Carrier Status"
          message="Select the carrier(s) who will launch your RCS campaign"
          error={mnoIdsError?.message}
        >
          <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
            <Flex sx={{ flexDirection: 'column', width: '100%', gap: 'xs' }}>
              {carriers.map((carrier) => (
                <CarrierStatus
                  key={carrier.id}
                  checked={mnoIds.includes(carrier.id)}
                  carrier={carrier}
                  onClick={() => handleCarrierToggle(carrier.id)}
                />
              ))}
            </Flex>
          </Flex>
        </FormSection>
        <FormActionLayout>
          <Button variant="outline" onClick={onPrevious}>
            Previous
          </Button>
          {DRAFT_RCS_CAMPAIGN_ENABLED && (
            <Button variant="outline" onClick={onSave}>
              Save Draft
            </Button>
          )}
          <Button
            type="submit"
            disabled={Object.values(errors).some((error) => !!error?.message)}
          >
            Next
          </Button>
        </FormActionLayout>
      </FormContainerLayout>
    </form>
  );
};

export default CarrierTermsForm;
